import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { keepAppLayersOpen } from 'features/tours/guideEvents'
import styled from 'styled-components'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { scrollAnchorIntoView } from './anchors'

/*
 * This app does not use MUI's default dialog z-index (1300). Its dialogs and
 * popovers sit at 99999–100300, a few modals and selects at 999999, toasts at
 * 99999999, and file previews at 2147483647. A tour has to be able to point at
 * a control inside a dialog — step two of the units tour lives in the
 * reconciliation modal — so it goes above all of those, and stays below
 * toasts so a notification still reaches the user mid-tour.
 */
const TOUR_Z = 9999999

const GAP = 14
const MARGIN = 12
const RING_PADDING = 6
const TIP_WIDTH = 328
const MOBILE_BREAKPOINT = 640

const OTHER_LAYERS = '.MuiDialog-root, .MuiPopover-root, .MuiMenu-root, .MuiDrawer-modal, [role="listbox"], [role="menu"], [aria-modal="true"]'

const isShown = el => {
  for (let node = el; node && node !== document.body; node = node.parentElement) {
    if (node.classList.contains('MuiModal-hidden') || node.getAttribute('aria-hidden') === 'true') return false
    const style = window.getComputedStyle(node)
    if (style.visibility === 'hidden' || style.display === 'none') return false
  }
  return true
}

export const otherLayerOpen = () => Array.from(document.querySelectorAll(OTHER_LAYERS)).some(isShown)

const rectOf = el => {
  if (!el || !el.isConnected) return null
  const r = el.getBoundingClientRect()
  return { top: r.top, left: r.left, width: r.width, height: r.height }
}

const sameRect = (a, b) =>
  a &&
  b &&
  a.top === b.top &&
  a.left === b.left &&
  a.width === b.width &&
  a.height === b.height

const TourSpotlight = ({
  step,
  anchorEl,
  stepNumber,
  stepCount,
  onNext,
  onDismiss,
  onCtaClick,
  mode = 'hint',
  onBack,
  onEscape,
  onFinish,
  advanceType = 'next',
  isStop = false,
  bottomOffset = 0,
  docked = false,
  onLowAnchor
}) => {
  const workflow = mode === 'workflow'
  // Measured synchronously so the first paint already has a position. The
  // rAF loop below only tracks movement — and rAF does not run in a hidden
  // tab, which would otherwise leave a started tour invisible until the user
  // switched back.
  const [rect, setRect] = useState(() => rectOf(anchorEl))
  const [tipSize, setTipSize] = useState({ width: TIP_WIDTH, height: 0 })
  const [isMobile, setIsMobile] = useState(
    () => window.innerWidth < MOBILE_BREAKPOINT
  )
  const tipRef = useRef(null)
  const bottomOffsetRef = useRef(bottomOffset)
  bottomOffsetRef.current = bottomOffset
  const onLowAnchorRef = useRef(onLowAnchor)
  onLowAnchorRef.current = onLowAnchor

  useLayoutEffect(() => {
    setRect(prev => {
      const next = rectOf(anchorEl)
      return sameRect(prev, next) ? prev : next
    })
    return scrollAnchorIntoView(anchorEl, workflow ? bottomOffsetRef.current : 0)
  }, [anchorEl, workflow])

  /*
   * Polling the rect each frame rather than listening for scroll/resize: the
   * anchor can also move because a dialog above it finished loading, a list
   * virtualised, or a CSS transition is still running. One rAF loop for the
   * duration of a tour is cheaper than getting any of those cases wrong.
   */
  useEffect(() => {
    let frame = null
    const tick = () => {
      const next = rectOf(anchorEl)
      if (next) setRect(prev => (sameRect(prev, next) ? prev : next))
      frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [anchorEl])

  useLayoutEffect(() => {
    if (!tipRef.current) return
    const { width, height } = tipRef.current.getBoundingClientRect()
    setTipSize(prev =>
      prev.width === width && prev.height === height ? prev : { width, height }
    )
  }, [step, rect, isMobile])

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const low = !!(
    workflow &&
    rect &&
    bottomOffset > 0 &&
    rect.top + rect.height >
      window.innerHeight - bottomOffset - (isMobile ? tipSize.height + MARGIN : 0)
  )

  useEffect(() => {
    if (onLowAnchorRef.current) onLowAnchorRef.current(low)
  }, [low])

  useEffect(() => () => {
    if (onLowAnchorRef.current) onLowAnchorRef.current(false)
  }, [])

  useEffect(() => {
    const onKey = e => {
      if (e.key !== 'Escape') return
      if (!workflow) {
        onDismiss()
        return
      }
      if (e.defaultPrevented || otherLayerOpen()) return
      if (onEscape) onEscape()
      else onDismiss()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onDismiss, workflow, onEscape])

  useEffect(() => {
    if (workflow && tipRef.current) tipRef.current.focus({ preventScroll: true })
  }, [workflow, step])

  if (!step || !rect) return null

  const hole = {
    top: Math.max(0, rect.top - RING_PADDING),
    left: Math.max(0, rect.left - RING_PADDING),
    width: rect.width + RING_PADDING * 2,
    height: rect.height + RING_PADDING * 2
  }
  const holeRight = hole.left + hole.width
  const holeBottom = hole.top + hole.height

  const vw = window.innerWidth
  const vh = window.innerHeight

  const floor = vh - MARGIN - (workflow && !docked ? bottomOffset : 0)
  const ceiling = MARGIN + (workflow && docked ? bottomOffset : 0)
  let placement = step.placement === 'top' ? 'top' : 'bottom'
  let tipTop = holeBottom + GAP
  if (placement === 'bottom' && tipTop + tipSize.height > floor) {
    placement = 'top'
  }
  if (placement === 'top') {
    tipTop = hole.top - GAP - tipSize.height
    // Flip back rather than run off the top edge; a tooltip half off screen is
    // worse than one on the less-preferred side.
    if (tipTop < ceiling) {
      placement = 'bottom'
      tipTop = holeBottom + GAP
    }
  }

  const anchorCentre = hole.left + hole.width / 2
  const rawLeft = anchorCentre - tipSize.width / 2
  const tipLeft = Math.min(
    Math.max(MARGIN, rawLeft),
    Math.max(MARGIN, vw - tipSize.width - MARGIN)
  )
  const arrowLeft = Math.min(
    Math.max(16, anchorCentre - tipLeft),
    Math.max(16, tipSize.width - 16)
  )

  const isLastStep = stepNumber >= stepCount

  return createPortal(
    <>
      {/* Blocks stray clicks everywhere except the highlighted control, which
          stays clickable so the user can just do the thing being explained. */}
      {!workflow && (
        <Backdrop
          onClick={onDismiss}
          style={{
            clipPath: `polygon(0 0, 0 100%, ${hole.left}px 100%, ${hole.left}px ${hole.top}px, ${holeRight}px ${hole.top}px, ${holeRight}px ${holeBottom}px, ${hole.left}px ${holeBottom}px, ${hole.left}px 100%, 100% 100%, 100% 0)`
          }}
        />
      )}
      <Ring
        $workflow={workflow}
        style={{
          top: hole.top,
          left: hole.left,
          width: hole.width,
          height: hole.height
        }}
      />
      {workflow && (
        <Outline
          style={{
            top: hole.top,
            left: hole.left,
            width: hole.width,
            height: hole.height
          }}
        />
      )}
      <Tip
        ref={tipRef}
        {...keepAppLayersOpen}
        role='dialog'
        aria-live='polite'
        aria-label={step.title}
        tabIndex={workflow ? -1 : undefined}
        $mobile={isMobile}
        $workflow={workflow}
        $bottomOffset={bottomOffset}
        $docked={docked}
        style={
          isMobile
            ? undefined
            : { top: tipTop, left: tipLeft, width: TIP_WIDTH }
        }
      >
        {!isMobile && <Arrow $placement={placement} style={{ left: arrowLeft }} />}
        <TipHead>
          <TipTitle>{step.title}</TipTitle>
          <CloseButton
            type='button'
            onClick={workflow && onEscape ? onEscape : onDismiss}
            aria-label={workflow ? 'Stop the guide' : 'Close'}
          >
            <CloseRoundedIcon style={{ fontSize: 18 }} />
          </CloseButton>
        </TipHead>
        <TipBody>{step.body}</TipBody>
        {workflow && isStop && (
          <TipBody>Nothing is saved until you press it yourself.</TipBody>
        )}
        <TipFoot>
          {!workflow && stepCount > 1 ? (
            <StepCount>
              Step {stepNumber} of {stepCount}
            </StepCount>
          ) : (
            <span />
          )}
          <TipActions>
            {workflow ? (
              <>
                {stepNumber > 1 && onBack && (
                  <SkipButton type='button' onClick={onBack}>Back</SkipButton>
                )}
                {isStop ? (
                  <CtaButton type='button' onClick={onFinish}>Finish guide</CtaButton>
                ) : advanceType === 'next' ? (
                  <CtaButton type='button' onClick={onNext}>{step.cta?.label || 'Next'}</CtaButton>
                ) : (
                  <WaitHint>{advanceType === 'appear' ? 'Carry on when you are ready.' : 'Click the highlighted part to carry on.'}</WaitHint>
                )}
              </>
            ) : (
              <>
                {!isLastStep && (
                  <SkipButton type='button' onClick={onDismiss}>
                    Skip
                  </SkipButton>
                )}
                <CtaButton type='button' onClick={onCtaClick}>
                  {step.cta?.label || (isLastStep ? 'Got it' : 'Next')}
                </CtaButton>
              </>
            )}
          </TipActions>
        </TipFoot>
      </Tip>
    </>,
    document.body
  )
}

export default TourSpotlight

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${TOUR_Z};
  background: transparent;
`

const Ring = styled.div`
  position: fixed;
  z-index: ${({ $workflow }) => ($workflow ? 99990 : TOUR_Z)};
  border-radius: 10px;
  pointer-events: none;
  box-shadow: 0 0 0 9999px rgba(12, 18, 41, 0.55),
    0 0 0 2px rgba(255, 255, 255, 0.9);
  transition: top 120ms ease, left 120ms ease, width 120ms ease,
    height 120ms ease;
`

const Outline = styled.div`
  position: fixed;
  z-index: ${TOUR_Z};
  border-radius: 10px;
  pointer-events: none;
  box-shadow: 0 0 0 2px #fff, 0 0 0 4px #33becc;
`

const Tip = styled.div`
  position: fixed;
  z-index: ${TOUR_Z + 1};
  background: ${({ theme }) => theme.colors.haiti};
  color: #fff;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 18px 40px rgba(12, 18, 41, 0.35);

  ${({ $mobile, $bottomOffset = 0, $docked }) =>
    $mobile &&
    ($docked
      ? `
    left: 12px;
    right: 12px;
    top: calc(${12 + $bottomOffset}px + env(safe-area-inset-top, 0px));
    bottom: auto;
    width: auto;
  `
      : `
    left: 12px;
    right: 12px;
    bottom: calc(${12 + $bottomOffset}px + env(safe-area-inset-bottom, 0px));
    top: auto;
    width: auto;
  `)}

  ${({ $workflow }) => $workflow && `
    h3 { font-size: 17px; }
    p { font-size: 15px; }
    button { min-height: 40px; font-size: 15px; }
  `}
`

const WaitHint = styled.span`
  font-size: 15px;
  color: rgba(255, 255, 255, 0.82);
`

const Arrow = styled.span`
  position: absolute;
  width: 12px;
  height: 12px;
  margin-left: -6px;
  transform: rotate(45deg);
  background: ${({ theme }) => theme.colors.haiti};
  ${({ $placement }) =>
    $placement === 'top' ? 'bottom: -5px;' : 'top: -5px;'}
`

const TipHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`

const TipTitle = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.35;
`

const CloseButton = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin: -4px -4px 0 0;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
  }
`

const TipBody = styled.p`
  margin: 8px 0 0;
  font-size: 13px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.82);
`

const TipFoot = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 16px;
`

const StepCount = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.55);
`

const TipActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const SkipButton = styled.button`
  padding: 7px 10px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
  cursor: pointer;

  &:hover {
    color: #fff;
  }
`

const CtaButton = styled.button`
  padding: 7px 14px;
  border: none;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    filter: brightness(1.08);
  }
`
